import React, { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import type { EntityClickHandler } from "../../types/entityRegistry";
import {
  EntityAddress,
  readDetailEntity,
  readDetailRelationship,
  withDetailEntity,
  withoutDetailEntity,
  withoutDetailRelationship,
} from "../../utils/entityAddress";

// What a click lands on that must not close the overlay: the sidebar, the
// overlay itself, what the component library renders outside the page
// (menus, popovers, dialogs, tooltips), and every control of the page. A
// control changes the page's own state in the address; closing on the same
// click would write the address a second time and undo that change.
const KEEPS_OVERLAY_OPEN = [
  "[data-sidebar]",
  "[data-overlay-content]",
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "label",
  '[role="button"]',
  '[role="tab"]',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="option"]',
  '[role="menuitem"]',
  ".MuiPopover-root",
  ".MuiMenu-root",
  ".MuiModal-root",
  ".MuiDialog-root",
  ".MuiTooltip-popper",
  ".MuiAutocomplete-popper",
].join(", ");

export interface EntityOverlay {
  // The entity the address names; null while the overlay is closed
  detail: EntityAddress | null;
  // Opens an entity in the overlay: its id and its type go into the address
  open: EntityClickHandler;
  // Closes the overlay: id and type leave the address
  close: () => void;
  // The relationship the address names, opened from an entity's relation
  // table; null while none is open. It and the entity exclude each other.
  relationshipId: string | null;
  // Closes the relationship's overlay: its id leaves the address
  closeRelationship: () => void;
  // The click handler of the page around the overlay: a click outside the
  // overlay closes it
  handlePageClick: (event: React.MouseEvent) => void;
}

/**
 * The detail overlay of a page, held in the address.
 *
 * The id and the type of the open entity are two query parameters beside
 * the page's own, which are kept; so is the id of an open relationship.
 * Opening and closing are entries of the browser's history, so back and
 * forward step through the details opened.
 */
export function useEntityOverlay(): EntityOverlay {
  const [searchParams, setSearchParams] = useSearchParams();

  const detail = useMemo(() => readDetailEntity(searchParams), [searchParams]);
  const relationshipId = readDetailRelationship(searchParams);

  // The click that opens an entity reaches the page's handler afterwards;
  // it must not close what it has just opened.
  const opening = useRef(false);

  const open = useCallback<EntityClickHandler>(
    (entityId, entityType) => {
      opening.current = true;
      setTimeout(() => {
        opening.current = false;
      }, 0);
      setSearchParams((current) =>
        withDetailEntity(current, entityId, entityType),
      );
    },
    [setSearchParams],
  );

  const close = useCallback(() => {
    setSearchParams((current) => withoutDetailEntity(current));
  }, [setSearchParams]);

  const closeRelationship = useCallback(() => {
    setSearchParams((current) => withoutDetailRelationship(current));
  }, [setSearchParams]);

  const handlePageClick = useCallback(
    (event: React.MouseEvent) => {
      if ((!detail && !relationshipId) || opening.current) {
        return;
      }
      const target = event.target as HTMLElement;
      if (target.closest(KEEPS_OVERLAY_OPEN)) {
        return;
      }
      if (detail) {
        close();
      } else {
        closeRelationship();
      }
    },
    [detail, relationshipId, close, closeRelationship],
  );

  return {
    detail,
    open,
    close,
    relationshipId,
    closeRelationship,
    handlePageClick,
  };
}
